import { useEffect, useState } from "react";
import { Trash2, Download, Mail, Plus, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import api from "@/lib/api";
import { SectionTitle, Card, Field, Area, SaveButton } from "../ui";

const SELECT = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const EMPTY = { title: "", cover_image: "", body: "", status: "draft" };

const fmt = (iso) => { try { return format(new Date(iso), "MMM d, yyyy"); } catch { return ""; } };

export default function NewsletterManager() {
  const [tab, setTab] = useState("issues");
  return (
    <div data-testid="admin-newsletter">
      <div className="flex gap-2 mb-6">
        {[["issues", "Issues"], ["subscribers", "Subscribers"]].map(([id, label]) => (
          <button key={id} data-testid={`newsletter-tab-${id}`} onClick={() => setTab(id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === id ? "bg-charcoal-800 text-white" : "bg-sand-100 text-charcoal-600 hover:bg-sand-200"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "issues" ? <IssuesTab /> : <SubscribersTab />}
    </div>
  );
}

function IssuesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/newsletters").then((res) => setRows(res.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/newsletters/${editing.id}`, editing);
      else await api.post("/admin/newsletters", editing);
      toast.success(editing.status === "published" ? "Published — it's live on /updates" : "Saved as draft");
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this newsletter?")) return;
    await api.delete(`/admin/newsletters/${id}`);
    toast.success("Newsletter deleted");
    load();
  };

  const set = (k, v) => setEditing({ ...editing, [k]: v });

  if (editing) {
    return (
      <div data-testid="admin-newsletter-editor">
        <div className="flex items-center justify-between mb-6">
          <SectionTitle title={editing.id ? "Edit newsletter" : "New newsletter"} />
          <button data-testid="newsletter-editor-close" onClick={() => setEditing(null)} className="text-charcoal-500 hover:text-terracotta inline-flex items-center gap-1 text-sm"><X size={16} /> Cancel</button>
        </div>
        <div className="space-y-6">
          <Card>
            <div className="space-y-4">
              <Field label="Title" testid="newsletter-title" value={editing.title} onChange={(v) => set("title", v)} />
              <Field label="Featured image URL (optional)" testid="newsletter-cover" value={editing.cover_image} onChange={(v) => set("cover_image", v)} />
              <Area label="Body (Markdown supported: ## headings, **bold**, - lists, [links](/book))" testid="newsletter-body" value={editing.body} onChange={(v) => set("body", v)} rows={14} />
              <div>
                <label className="text-sm font-medium text-charcoal-600 mb-1.5 block">Status</label>
                <select data-testid="newsletter-status" className={SELECT} value={editing.status} onChange={(e) => set("status", e.target.value)}>
                  <option value="draft">Draft (hidden)</option>
                  <option value="published">Published (live on /updates)</option>
                </select>
              </div>
            </div>
          </Card>
          <SaveButton testid="newsletter-save" onClick={save} saving={saving} children={editing.status === "published" ? "Save & publish" : "Save draft"} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-newsletter-issues">
      <div className="flex items-center justify-between mb-6">
        <SectionTitle title="Newsletter" subtitle="Write updates for your audience. Published issues appear instantly on the public /updates page." />
        <button data-testid="newsletter-new" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
          <Plus size={16} /> New newsletter
        </button>
      </div>
      <div className="space-y-4">
        {rows.length === 0 && <p className="text-charcoal-500">No newsletters yet.</p>}
        {rows.map((n) => (
          <Card key={n.id}>
            <div className="flex gap-4 items-center">
              {n.cover_image && <img src={n.cover_image} alt={n.title} className="h-16 w-24 shrink-0 rounded-xl object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-charcoal-800 truncate">{n.title}</h3>
                  <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${n.status === "published" ? "bg-sage-light text-sage-700" : "bg-sand-200 text-charcoal-500"}`}>
                    {n.status}
                  </span>
                </div>
                <p className="text-sm text-charcoal-500">{n.status === "published" ? `Published ${fmt(n.published_at)}` : `Created ${fmt(n.created_at)}`}</p>
              </div>
              <button data-testid={`newsletter-edit-${n.id}`} onClick={() => setEditing({ ...EMPTY, ...n })} className="text-charcoal-400 hover:text-terracotta px-2"><Pencil size={16} /></button>
              <button data-testid={`newsletter-delete-${n.id}`} onClick={() => remove(n.id)} className="text-charcoal-400 hover:text-destructive px-2"><Trash2 size={16} /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function SubscribersTab() {
  const [rows, setRows] = useState([]);

  const load = () => api.get("/admin/newsletter").then((res) => setRows(res.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/admin/newsletter/${id}`);
    toast.success("Subscriber removed");
    load();
  };

  const exportCsv = () => {
    const header = "email,source,subscribed_at\n";
    const body = rows.map((r) => `${r.email},${r.source || ""},${r.created_at || ""}`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hola-movers-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-newsletter-subscribers">
      <div className="flex items-center justify-between mb-6">
        <SectionTitle title="Subscribers" subtitle="People who subscribed for move-to-Mexico tips. Export to use with your email tool." />
        {rows.length > 0 && (
          <button data-testid="newsletter-export" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-charcoal-300 px-5 py-2 text-sm font-medium text-charcoal-700 hover:bg-sand-200">
            <Download size={16} /> Export CSV
          </button>
        )}
      </div>
      <Card>
        {rows.length === 0 ? (
          <p className="text-charcoal-500 flex items-center gap-2"><Mail size={16} /> No subscribers yet.</p>
        ) : (
          <div className="divide-y divide-sand-100">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2.5" data-testid={`subscriber-${r.id}`}>
                <div>
                  <p className="text-sm text-charcoal-800">{r.email}</p>
                  <p className="text-xs text-charcoal-500">{r.source || "site"} · {fmt(r.created_at)}</p>
                </div>
                <button onClick={() => remove(r.id)} className="p-1 text-charcoal-400 hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
